import { useCallback } from 'react';
import intl from 'react-intl-universal';
import { PROJECT_STATUS } from 'utils/constants/projectsList';
import { ProjectStatus } from 'utils/types/projectsList';
import { useAppThunkDispatch } from 'state/store';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { updateProjectInList } from 'state/ProjectsList/projectsListSlice';
import { StatusOption } from 'Atoms/EditableTableRowCell/StatusTableRowCell/StatusTableRowCell';
import { bulkUpdateProjects } from 'api/projects';
import { toUtcNoonIso } from 'utils/calendarDates';

interface ProjectWithStatus {
  id: string;
  status?: string;
  title?: string;
  name?: string;
  is_archived?: boolean;
  areAllTasksCompleted?: boolean;
  number_of_not_completed_tasks?: number;
  taskCount?: number;
  completedTaskCount?: number;
  progress?: {
    totalTasks?: number;
    completedTasks?: number;
  };
}

interface UseUpdateProjectStatusReturn {
  handleSaveProjectStatus: (
    newValue: string,
    canUpdateStatus: boolean,
    project: ProjectWithStatus,
    onOpenCancelModal?: (projectId: string) => void,
    onOpenOnHoldModal?: (projectId: string) => void,
    onOpenCloseModal?: (projectId: string) => void,
    onRefresh?: () => void,
    onUpdateSuccess?: (projectId: string, newStatus: string) => void
  ) => void;
  getProjectStatuses: (project: ProjectWithStatus) => StatusOption[];
}

export const useUpdateProjectStatus = (): UseUpdateProjectStatusReturn => {
  const dispatch = useAppThunkDispatch();

  const handleSaveProjectStatus = useCallback(
    async (
      newValue: string,
      canUpdateStatus: boolean,
      project: ProjectWithStatus,
      onOpenCancelModal?: (projectId: string) => void,
      onOpenOnHoldModal?: (projectId: string) => void,
      onOpenCloseModal?: (projectId: string) => void,
      onRefresh?: () => void,
      onUpdateSuccess?: (projectId: string, newStatus: string) => void
    ) => {
      const { id } = project;

      if (!canUpdateStatus || newValue === project.status) {
        return;
      }

      if (newValue === PROJECT_STATUS.CANCELED) {
        if (onOpenCancelModal) {
          onOpenCancelModal(id);
        }

        return;
      }

      if (newValue === PROJECT_STATUS.ON_HOLD) {
        if (onOpenOnHoldModal) {
          onOpenOnHoldModal(id);
        }

        return;
      }

      if (newValue === PROJECT_STATUS.CLOSED) {
        if (onOpenCloseModal) {
          onOpenCloseModal(id);
        }

        return;
      }

      // CGP-247: completing a project inline has no modal to collect a date, so
      // stamp "today" here. Every read of actualDate renders it as a UTC date —
      // the projects service serializes it via ProjectDate.toString() in the UTC
      // server process, the insights views ::date-cast it, and both CSV
      // exporters format it server-side — so a raw toISOString() of a local
      // instant lands a calendar day off outside UTC. toUtcNoonIso stamps the
      // user's local calendar day at UTC noon, which reads back as that same day
      // from UTC-11 to UTC+11 (CP-10907).
      const today = new Date();
      const actualCompletionDate =
        newValue === PROJECT_STATUS.COMPLETED ? toUtcNoonIso(today) : undefined;

      const response = await bulkUpdateProjects({
        ids: [id],
        status: newValue,
        ...(actualCompletionDate && { actualCompletionDate }),
      });

      const data = Array.isArray(response.data)
        ? response.data[0]
        : response.data;
      const { successfulCount, failedCount } = data.attributes;

      if (successfulCount > 0) {
        if (onUpdateSuccess) {
          onUpdateSuccess(id, newValue);
        } else if (onRefresh) {
          onRefresh();
        } else {
          dispatch(
            updateProjectInList({
              projectId: id,
              updatedProject: {
                status: newValue as ProjectStatus,
                // The list API serializes this column as a localized date string
                // (ProjectDate.toString()), not an ISO instant, and the cell
                // renders it through formatColumnDateValue -> new Date(). Patch
                // the store in that same shape — a bare ISO date would parse as
                // UTC midnight and render a day early west of UTC. Read the day
                // off `today` rather than re-deriving it from the UTC-noon stamp,
                // which would land on the next day at UTC+12 and beyond.
                //
                // Leaving Completed must blank the column instead, or the row
                // keeps a stale completion date until a refetch. That clear
                // can't ride the wire — the service forbids
                // actualCompletionDate on non-Completed transitions and
                // clears the column itself on exit (projects#113) — so it is
                // store-only: '' renders blank via formatColumnDateValue. Any
                // other transition must leave the key absent so the shallow
                // patch keeps the row's existing value.
                ...((actualCompletionDate ||
                  project.status === PROJECT_STATUS.COMPLETED) && {
                  actualCompletionDate: actualCompletionDate
                    ? today.toLocaleDateString('en-US')
                    : '',
                }),
              },
            })
          );
        }
      } else if (failedCount > 0) {
        dispatch(
          showSnackbarNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get(
              'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
            ),
            notificationMessage: intl.get(
              'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
            ),
          })
        );
      }
    },
    [dispatch]
  );

  const getProjectStatuses = useCallback((project: ProjectWithStatus) => {
    const currentStatus = project.status;
    const isArchived = project.is_archived;
    let areAllTasksCompleted = project.areAllTasksCompleted;

    if (areAllTasksCompleted === undefined) {
      if (
        project.taskCount !== undefined &&
        project.completedTaskCount !== undefined
      ) {
        areAllTasksCompleted =
          project.taskCount > 0 &&
          project.taskCount === project.completedTaskCount;
      } else if (project.number_of_not_completed_tasks !== undefined) {
        areAllTasksCompleted = project.number_of_not_completed_tasks === 0;
      } else if (project.progress) {
        const { totalTasks = 0, completedTasks = 0 } = project.progress;

        areAllTasksCompleted = totalTasks > 0 && totalTasks === completedTasks;
      }
    }

    return Object.keys(PROJECT_STATUS).map((key) => {
      const statusValue = PROJECT_STATUS[key as keyof typeof PROJECT_STATUS];
      const option: StatusOption = {
        label: intl.get(`PROJECT_DETAIL.STATUS_OPTIONS.${key}`),
        value: statusValue,
        disabled: false,
        message: undefined,
      };

      if (isArchived && statusValue !== currentStatus) {
        option.disabled = true;
        option.message = intl.get('PROJECT_DETAIL.ARCHIVED_STATUS_DISABLED');

        return option;
      }

      switch (statusValue) {
        case PROJECT_STATUS.NEW:
          if (currentStatus === PROJECT_STATUS.CLOSED) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.NEW_DISABLED_FROM_CLOSED'
            );
          }

          break;

        case PROJECT_STATUS.IN_PLANNING:
          if (currentStatus === PROJECT_STATUS.CLOSED) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.IN_PLANNING_DISABLED_FROM_CLOSED'
            );
          }

          break;

        case PROJECT_STATUS.IN_PROGRESS:
          if (currentStatus === PROJECT_STATUS.CLOSED) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.IN_PROGRESS_DISABLED_FROM_CLOSED'
            );
          }

          break;

        case PROJECT_STATUS.ON_HOLD:
          if (currentStatus === PROJECT_STATUS.CLOSED) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.ON_HOLD_DISABLED_FROM_CLOSED'
            );
          }

          break;

        case PROJECT_STATUS.CANCELED:
          break;

        case PROJECT_STATUS.COMPLETED:
          if (currentStatus === PROJECT_STATUS.CANCELED) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.COMPLETED_DISABLED_FROM_CANCELED'
            );
          } else if (currentStatus === PROJECT_STATUS.CLOSED) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.COMPLETED_DISABLED_FROM_CLOSED'
            );
          } else if (areAllTasksCompleted === false) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.COMPLETED_DISABLED_TASKS_INCOMPLETE'
            );
          }

          break;

        case PROJECT_STATUS.CLOSED:
          if (
            currentStatus !== PROJECT_STATUS.COMPLETED &&
            currentStatus !== PROJECT_STATUS.CLOSED
          ) {
            option.disabled = true;
            option.message = intl.get(
              'PROJECT_DETAIL.CLOSED_ONLY_FROM_COMPLETED'
            );
          }

          break;

        default:
          break;
      }

      return option;
    });
  }, []);

  return {
    handleSaveProjectStatus,
    getProjectStatuses,
  };
};
